import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { BiLock } from "react-icons/bi";
import { isSecretKey } from "./onboarding";

const IntegrationKeysDialog = ({
  title,
  vendor,
  providers,
  provider,
  keys,
  entered,
  present,
  locked,
  labels,
  onProvider,
  onChange,
  onClose,
}) => {
  const dialogRef = useRef(null);
  const [shown, setShown] = useState({});

  useEffect(() => {
    if (dialogRef.current) dialogRef.current.focus();
  }, []);

  const closeOnEscape = (event) => {
    if (event.key !== "Escape") return;
    event.stopPropagation();
    onClose();
  };

  return (
    <>
      <div className="onboarding__backdrop" onClick={onClose} />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="onboarding-keys-title"
        tabIndex={-1}
        className="onboarding__dialog"
        onKeyDown={closeOnEscape}
      >
        <div className="onboarding__dialog-head">
          <h2 id="onboarding-keys-title" className="onboarding__main-title">
            {`${title} · ${vendor}`}
          </h2>
          {locked ? null : <p className="onboarding__hint">{labels.HINT}</p>}
        </div>
        <div className="onboarding__dialog-body">
          {providers.length > 1 ? (
            <div className="onboarding__field">
              <label htmlFor="onboarding-key-provider">{labels.PROVIDER}</label>
              <select
                id="onboarding-key-provider"
                className="onboarding__input"
                value={provider}
                onChange={({ target }) => onProvider(target.value)}
              >
                {providers.map((option) => (
                  <option key={option.key} value={option.key}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          {locked && keys.length > 0 ? (
            <p className="onboarding__notice onboarding__notice--info">
              <BiLock size={18} aria-hidden="true" />
              <span>{labels.LOCKED}</span>
            </p>
          ) : null}
          {keys.map((key) => {
            const secret = isSecretKey(key);
            const id = `onboarding-key-${key}`;
            return (
              <div key={key} className="onboarding__field">
                <label htmlFor={id} className="onboarding__key-label">
                  {key}
                </label>
                <div className="onboarding__key-row">
                  <input
                    id={id}
                    type={secret && !shown[key] ? "password" : "text"}
                    autoComplete="off"
                    spellCheck={false}
                    className="onboarding__input onboarding__input--mono"
                    disabled={locked}
                    value={entered[key] || ""}
                    placeholder={present.includes(key) ? labels.KEPT : ""}
                    onChange={({ target }) =>
                      onChange({ ...entered, [key]: target.value })
                    }
                  />
                  {secret && !locked ? (
                    <button
                      type="button"
                      className="onboarding__button onboarding__button--outline"
                      aria-pressed={Boolean(shown[key])}
                      onClick={() =>
                        setShown((current) => ({
                          ...current,
                          [key]: !current[key],
                        }))
                      }
                    >
                      {shown[key] ? labels.HIDE : labels.SHOW}
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
        <div className="onboarding__footer">
          <button
            type="button"
            className="onboarding__button onboarding__button--primary onboarding__push"
            onClick={onClose}
          >
            {labels.DONE}
          </button>
        </div>
      </div>
    </>
  );
};

IntegrationKeysDialog.propTypes = {
  title: PropTypes.string.isRequired,
  vendor: PropTypes.string,
  providers: PropTypes.arrayOf(PropTypes.object),
  provider: PropTypes.string,
  keys: PropTypes.arrayOf(PropTypes.string).isRequired,
  entered: PropTypes.object,
  present: PropTypes.arrayOf(PropTypes.string),
  locked: PropTypes.bool,
  labels: PropTypes.object.isRequired,
  onProvider: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

IntegrationKeysDialog.defaultProps = {
  vendor: "",
  providers: [],
  provider: "",
  entered: {},
  present: [],
  locked: false,
};

export default IntegrationKeysDialog;
